import _ from 'lodash';
import { Store } from '@/core/flux.service';
import { processMaterializedTable } from '@/storedTableEditor/storedTable.utilities';
import { type MaterializedTableOutput } from '@/tableDefinitionEditor/tableDefinition.types';
import type { GridApi } from 'ag-grid-enterprise';
import type { PersistenceLevel } from '@/core/flux.service';
import type {
  ProcessedMaterializedTable,
  StoredTableSearchFailure,
} from '@/tableDefinitionEditor/tableDefinition.types';
import type { StoredTableColumnOutputV1 } from '@seeqdev/api/model/StoredTableColumnOutputV1';
import type { StoredTableOutputV1 } from '@seeqdev/api/model/StoredTableOutputV1';

interface StoredTableState {
  tableId: string | undefined;
  storedTable: StoredTableOutputV1 | undefined;
  columns: StoredTableColumnOutputV1[];
  isLoadingStoredTable: boolean;
  name: string | undefined;
  materializedTable: ProcessedMaterializedTable | undefined;
  agGridApi?: GridApi;
  searchFailures: StoredTableSearchFailure[];
  showSearchFailuresModal: false;
  searchFailureMessages: string[];
}

export type StoredTableDehydratedState = Omit<
  StoredTableState,
  'storedTable' | 'materializedTable' | 'agGridApi' | 'columns' | 'name'
>;

/**
 * This store manages the state for the stored table editor, including the current table,
 * its columns, reload state, and other related editor state.
 */
export class StoredTableStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqStoredTableStore';

  initialize() {
    const initialState: StoredTableState = {
      tableId: undefined,
      storedTable: undefined,
      columns: [],
      isLoadingStoredTable: false,
      name: undefined,
      materializedTable: undefined,
      searchFailures: [],
      showSearchFailuresModal: false,
      searchFailureMessages: [],
    };
    this.state = this.immutable(initialState);
  }

  get tableId(): string | undefined {
    return this.state.get('tableId');
  }

  get storedTable(): StoredTableOutputV1 | undefined {
    return this.state.get('storedTable');
  }

  get columns(): StoredTableColumnOutputV1[] {
    return this.state.get('columns');
  }

  get isLoadingStoredTable(): boolean {
    return this.state.get('isLoadingStoredTable');
  }

  get name(): string | undefined {
    return this.state.get('name');
  }

  get agGridApi(): GridApi | undefined {
    return this.state.get('agGridApi');
  }

  get materializedTable(): ProcessedMaterializedTable | undefined {
    return this.state.get('materializedTable');
  }

  get searchFailures(): StoredTableSearchFailure[] {
    return this.state.get('searchFailures');
  }

  get showSearchFailuresModal(): boolean {
    return this.state.get('showSearchFailuresModal');
  }

  get searchFailureMessages(): string[] {
    return this.state.get('searchFailureMessages');
  }

  dehydrate(): StoredTableDehydratedState {
    return _.omit(this.state.serialize(), ['storedTable', 'materializedTable', 'agGridApi', 'columns', 'name']);
  }

  rehydrate(dehydratedState: Partial<StoredTableDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    STORED_TABLE_SET_TABLE_ID: (payload: { tableId: string }) => {
      this.state.set('tableId', payload.tableId);
    },
    SET_STORED_TABLE: (payload: { storedTable: StoredTableOutputV1 }) => {
      const storedTable = payload.storedTable;
      this.state.set('storedTable', storedTable);
      this.state.set('name', storedTable.name);
      this.state.set('columns', storedTable.columns);
    },
    RESET_STORED_TABLE: () => {
      this.initialize();
    },
    STORED_TABLE_SET_IS_LOADING_STORED_TABLE: (payload: { isLoadingStoredTable: boolean }) => {
      this.state.set('isLoadingStoredTable', payload.isLoadingStoredTable);
    },
    SET_STORED_TABLE_AG_GRID_API: (payload: { agGridApi: GridApi }) => {
      this.state.set('agGridApi', payload.agGridApi);
    },
    SET_STORED_TABLE_MATERIALIZED_TABLE: (payload: { materializedTable: MaterializedTableOutput }) => {
      const processedMaterializedTable = processMaterializedTable(payload.materializedTable, this.state.get('columns'));
      this.state.set('materializedTable', processedMaterializedTable);
    },
    SET_STORED_TABLE_NAME: (payload: { name: string }) => {
      this.state.set('name', payload.name);
    },
    SET_STORED_SEARCH_FAILURES: (payload: { searchFailures: StoredTableSearchFailure[] }) => {
      this.state.set('searchFailures', payload.searchFailures);
    },
    SET_SHOW_STORED_SEARCH_FAILURES_MODAL: (payload: { showSearchFailuresModal: boolean }) => {
      this.state.set('showSearchFailuresModal', payload.showSearchFailuresModal);
    },
    SET_STORED_SEARCH_FAILURE_MESSAGES: (payload: { messages: string[] }) => {
      this.state.set('searchFailureMessages', payload.messages);
    },
  };
}
